import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  getDocs,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { Edit2, Trash2, Plus, Search, Filter, X, Save, XCircle, Download, Upload } from 'react-feather';
import './AdminMenuPageRedesign.css';
import * as XLSX from 'xlsx';
import DatabaseCleaner from '../components/DatabaseCleaner';

const CATEGORIES = [
  'COFFEE',
  'NON-COFFEE',
  'MILKBASED',
  'DESSERT',
  'TOASTS_SLIDERS',
  'BOWLS',
  'PASTRIES',
];

const CATEGORY_DISPLAY_NAMES = {
  'COFFEE': 'Coffee',
  'NON-COFFEE': 'Non-Coffee',
  'MILKBASED': 'Milk Based',
  'DESSERT': 'Dessert',
  'TOASTS_SLIDERS': 'Toasts & Sliders',
  'BOWLS': 'Bowls',
  'PASTRIES': 'Pastries',
};

const AdminMenuPageRedesign = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('Branch 1');
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [showBranchManager, setShowBranchManager] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [managingBranchId, setManagingBranchId] = useState(null);
  const [showDatabaseCleaner, setShowDatabaseCleaner] = useState(false);
  const [branchCategories, setBranchCategories] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryOrder, setCategoryOrder] = useState({});
  const [customDisplayNames, setCustomDisplayNames] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('COFFEE');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    imageUrl: '',
    category: 'COFFEE',
    order: 0,
    isNew: false,
    isPopular: false,
    calories: '',
    ingredients: [],
    title_ar: '',
    description_ar: '',
    type: '',
    options: [],
  });

  // Fetch branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'branches'));
        const branchList = [];
        querySnapshot.forEach((doc) => {
          branchList.push({ id: doc.id, ...doc.data() });
        });
        
        // If no branches exist, create default ones
        if (branchList.length === 0) {
          const defaultBranches = [
            { id: 'branch1', name: 'Branch 1', originalId: 'Branch 1' },
            { id: 'branch2', name: 'Branch 2', originalId: 'Branch 2' }
          ];
          
          for (const branch of defaultBranches) {
            await addDoc(collection(db, 'branches'), branch);
          }
          setBranches(defaultBranches);
        } else {
          setBranches(branchList);
          // Set selected branch to first available branch if current selection doesn't exist
          if (branchList.length > 0) {
            const firstBranch = branchList[0];
            setSelectedBranch(firstBranch.originalId || firstBranch.name);
          }
          // Fetch categories for loaded branches
          const categories = {};
          branchList.forEach((branch) => {
            categories[branch.id] = branch.categories || CATEGORIES;
          });
          setBranchCategories(categories);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
        // Fallback to default branches
        setBranches([
          { id: 'branch1', name: 'Branch 1', originalId: 'Branch 1' },
          { id: 'branch2', name: 'Branch 2', originalId: 'Branch 2' }
        ]);
      } finally {
        setBranchesLoading(false);
      }
    };

    fetchBranches();
  }, []);

  // Update branch name
  const updateBranchName = async (branchId, newName) => {
    try {
      const branchDoc = doc(db, 'branches', branchId);
      await updateDoc(branchDoc, { name: newName });
      
      setBranches(prev => prev.map(branch => 
        branch.id === branchId ? { ...branch, name: newName } : branch
      ));
    } catch (error) {
      console.error('Error updating branch name:', error);
      alert('Failed to update branch name');
    }
  };

  // Fetch branch categories (from branches collection)
  const fetchBranchCategories = async () => {
    try {
      const categories = {};
      branches.forEach((branch) => {
        categories[branch.id] = branch.categories || CATEGORIES;
      });
      setBranchCategories(categories);
    } catch (error) {
      console.error('Error fetching branch categories:', error);
    }
  };

  // Update branch categories (store in branch document)
  const updateBranchCategories = async (branchId, categories) => {
    try {
      const branchDoc = doc(db, 'branches', branchId);
      await updateDoc(branchDoc, { 
        categories: categories,
        updatedAt: new Date()
      });
      
      // Update local state
      setBranches(prev => prev.map(branch => 
        branch.id === branchId ? { ...branch, categories } : branch
      ));
      setBranchCategories(prev => ({ ...prev, [branchId]: categories }));
      
      console.log('Successfully updated categories for branch:', branchId);
      alert('Categories updated successfully!');
    } catch (error) {
      console.error('Error updating branch categories:', error);
      alert(`Failed to update categories: ${error.message}`);
    }
  };

  // Open category manager for specific branch
  const openCategoryManager = (branchId) => {
    setManagingBranchId(branchId);
    setShowCategoryManager(true);
  };

  // Add new branch
  const addNewBranch = async (branchName) => {
    if (!branchName || !branchName.trim()) return;

    try {
      const newBranchData = {
        name: branchName.trim(),
        originalId: branchName.trim(),
        createdAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'branches'), newBranchData);
      
      // Update local state with the actual Firebase document ID
      setBranches(prev => [...prev, { id: docRef.id, ...newBranchData }]);
      
      alert('Branch added successfully!');
    } catch (error) {
      console.error('Error adding branch:', error);
      alert('Failed to add branch. Please try again.');
    }
  };

  // Delete branch
  const deleteBranch = async (branchId) => {
    if (branches.length <= 1) {
      alert('Cannot delete the last branch. You need at least one branch.');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this branch? This will also delete ALL menu items for this branch. This action cannot be undone.'
    );

    if (!confirmDelete) return;

    try {
      // Delete branch document
      await deleteDoc(doc(db, 'branches', branchId));
      
      // Delete all menu items for this branch
      const menuRef = collection(db, 'menuItems');
      const branchToDelete = branches.find(b => b.id === branchId);
      const q = query(menuRef, where('branchId', '==', branchToDelete.originalId || branchToDelete.name));
      const menuSnapshot = await getDocs(q);
      
      const deletePromises = menuSnapshot.docs.map(menuDoc => 
        deleteDoc(doc(db, 'menuItems', menuDoc.id))
      );
      await Promise.all(deletePromises);

      // Update local state
      setBranches(prev => prev.filter(branch => branch.id !== branchId));
      
      // If deleted branch was selected, switch to first remaining branch
      if ((branchToDelete.originalId || branchToDelete.name) === selectedBranch) {
        const remainingBranches = branches.filter(b => b.id !== branchId);
        setSelectedBranch(remainingBranches[0]?.originalId || remainingBranches[0]?.name || 'Branch 1');
      }

      alert('Branch and all associated menu items have been deleted successfully.');
    } catch (error) {
      console.error('Error deleting branch:', error);
      alert('Failed to delete branch. Please try again.');
    }
  };

  useEffect(() => {
    fetchMenuItems();
  }, [selectedBranch, selectedCategory]);

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const menuRef = collection(db, 'menuItems');
      const categoryToFetch = selectedCategory === 'DESSERT' ? 'FOOD' : selectedCategory;
      
      console.log('Fetching items with:', {
        selectedBranch,
        categoryToFetch,
        selectedCategory
      });
      
      const q = query(
        menuRef,
        where('branchId', '==', selectedBranch),
        where('category', '==', categoryToFetch)
      );
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      console.log('Found items:', items.length, items);
      setMenuItems(items.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error('Error fetching menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const categoryToSave = formData.category === 'DESSERT' ? 'FOOD' : formData.category;
      const itemData = {
        name: formData.name,
        price: parseFloat(formData.price),
        description: formData.description,
        imageUrl: formData.imageUrl,
        category: categoryToSave,
        branchId: selectedBranch,
        order: parseInt(formData.order) || 0,
        isNew: formData.isNew,
        isPopular: formData.isPopular,
        nutritionalInfo: {
          calories: formData.calories,
        },
        ingredients: formData.ingredients,
        updatedAt: new Date(),
        title_ar: formData.title_ar,
        description_ar: formData.description_ar,
        type: formData.type || '',
        options: formData.options || [],
      };

      if (editingItem) {
        await updateDoc(doc(db, 'menuItems', editingItem.id), itemData);
      } else {
        itemData.createdAt = new Date();
        await addDoc(collection(db, 'menuItems'), itemData);
      }

      fetchMenuItems();
      resetForm();
    } catch (error) {
      console.error('Error saving menu item:', error);
    }
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteDoc(doc(db, 'menuItems', itemId));
        fetchMenuItems();
      } catch (error) {
        console.error('Error deleting menu item:', error);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      price: item.price || '',
      description: item.description || '',
      imageUrl: item.imageUrl || '',
      category: item.category || 'COFFEE',
      order: item.order || 0,
      isNew: item.isNew || false,
      isPopular: item.isPopular || false,
      calories: item.nutritionalInfo?.calories || '',
      ingredients: item.ingredients || [],
      title_ar: item.title_ar || '',
      description_ar: item.description_ar || '',
      type: item.type || '',
      options: item.options || [],
    });
    setIsAddModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      description: '',
      imageUrl: '',
      category: 'COFFEE',
      order: 0,
      isNew: false,
      isPopular: false,
      calories: '',
      ingredients: [],
      title_ar: '',
      description_ar: '',
      type: '',
      options: [],
    });
    setEditingItem(null);
    setIsAddModalOpen(false);
  };

  const filteredItems = menuItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Preview first 5 rows
        setImportPreview(jsonData.slice(0, 5));
      } catch (error) {
        console.error('Error reading file:', error);
        alert('Error reading Excel file. Please make sure it\'s a valid Excel file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Import data from Excel
  const importFromExcel = async () => {
    if (!importFile) return;
    
    setImportLoading(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          let imported = 0;
          let errors = 0;
          
          for (const row of jsonData) {
            try {
              // Map Excel columns to our data structure
              const itemData = {
                name: row['Item Name (English)'] || row['Name'] || '',
                title_ar: row['Item Name (Arabic)'] || row['Arabic Name'] || '',
                description: row['Description (English)'] || row['Description'] || '',
                description_ar: row['Description (Arabic)'] || row['Arabic Description'] || '',
                price: parseFloat(row['Price (QAR)'] || row['Price'] || 0),
                category: (row['Category'] || '').toUpperCase(),
                branchId: row['Branch'] || selectedBranch,
                type: row['Type'] || '',
                order: parseInt(row['Display Order'] || row['Order'] || 0),
                isPopular: (row['Popular'] || '').toLowerCase() === 'yes',
                isNew: (row['New'] || '').toLowerCase() === 'yes',
                imageUrl: row['Image URL'] || '',
                nutritionalInfo: {
                  calories: row['Calories'] || ''
                },
                ingredients: row['Ingredients'] ? row['Ingredients'].split(',').map(i => i.trim()) : [],
                createdAt: new Date(),
                updatedAt: new Date()
              };
              
              // Debug logging
              console.log('Importing item:', {
                name: itemData.name,
                branchId: itemData.branchId,
                selectedBranch: selectedBranch,
                category: itemData.category
              });
              
              // Validate required fields
              if (!itemData.name || !itemData.price || !itemData.category) {
                errors++;
                continue;
              }
              
              // Convert DESSERT to FOOD for storage
              if (itemData.category === 'DESSERT') {
                itemData.category = 'FOOD';
              }
              
              await addDoc(collection(db, 'menuItems'), itemData);
              imported++;
            } catch (error) {
              console.error('Error importing row:', error);
              errors++;
            }
          }
          
          alert(`Import completed!\nImported: ${imported} items\nErrors: ${errors} items`);
          fetchMenuItems();
          setShowImportModal(false);
          setImportFile(null);
          setImportPreview([]);
          
        } catch (error) {
          console.error('Error processing file:', error);
          alert('Error processing Excel file. Please check the format and try again.');
        } finally {
          setImportLoading(false);
        }
      };
      reader.readAsArrayBuffer(importFile);
    } catch (error) {
      console.error('Import error:', error);
      alert('Error importing data. Please try again.');
      setImportLoading(false);
    }
  };

  // Load custom categories and display names
  const loadCustomCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const categories = [];
      const order = {};
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        categories.push({ id: doc.id, ...data });
        order[data.name] = data.order || 0;
      });
      setCustomCategories(categories.sort((a, b) => (a.order || 0) - (b.order || 0)));
      setCategoryOrder(order);

      // Load custom display names for default categories
      const displayNamesSnapshot = await getDocs(collection(db, 'categoryDisplayNames'));
      const displayNames = {};
      displayNamesSnapshot.forEach((doc) => {
        displayNames[doc.id] = doc.data().displayName;
      });
      setCustomDisplayNames(displayNames);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Update default category display name
  const updateDefaultCategoryName = async (categoryKey, newDisplayName) => {
    try {
      await setDoc(doc(db, 'categoryDisplayNames', categoryKey), {
        displayName: newDisplayName,
        updatedAt: new Date()
      });
      setCustomDisplayNames(prev => ({ ...prev, [categoryKey]: newDisplayName }));
    } catch (error) {
      console.error('Error updating category display name:', error);
      alert('Failed to update category name');
    }
  };

  // Add new category
  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const categoryData = {
        name: newCategoryName.toUpperCase().replace(/\s+/g, '_'),
        displayName: newCategoryName,
        order: customCategories.length,
        createdAt: new Date()
      };
      await addDoc(collection(db, 'categories'), categoryData);
      setNewCategoryName('');
      loadCustomCategories();
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };

  // Update category
  const updateCategory = async (categoryId, updates) => {
    try {
      await updateDoc(doc(db, 'categories', categoryId), updates);
      loadCustomCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };

  // Delete category
  const deleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the "${categoryName}" category? This will affect all menu items in this category.`)) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'categories', categoryId));
      loadCustomCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  // Move category up/down
  const moveCategoryOrder = async (categoryId, direction) => {
    try {
      const categoryIndex = customCategories.findIndex(cat => cat.id === categoryId);
      if (categoryIndex === -1) return;

      const category = customCategories[categoryIndex];
      const newOrder = direction === 'up' ? category.order - 1 : category.order + 1;
      
      if (newOrder < 0 || newOrder >= customCategories.length) return;

      // Find the category to swap with
      const swapCategory = customCategories.find(cat => cat.order === newOrder);
      if (swapCategory) {
        await updateDoc(doc(db, 'categories', category.id), { order: newOrder });
        await updateDoc(doc(db, 'categories', swapCategory.id), { order: category.order });
        loadCustomCategories();
      }
    } catch (error) {
      console.error('Error moving category:', error);
      alert('Failed to move category');
    }
  };

  // Get all available categories (default + custom)
  const getAllCategories = () => {
    const defaultCategories = CATEGORIES.map(cat => ({
      name: cat,
      displayName: CATEGORY_DISPLAY_NAMES[cat] || cat,
      isDefault: true
    }));
    const custom = customCategories.map(cat => ({
      name: cat.name,
      displayName: cat.displayName || cat.name,
      isDefault: false,
      id: cat.id,
      order: cat.order
    }));
    return [...defaultCategories, ...custom].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return (a.order || 0) - (b.order || 0);
    });
  };

  // Load categories on component mount
  useEffect(() => {
    loadCustomCategories();
  }, []);

  // Update item order
  const updateItemOrder = async (itemId, direction) => {
    try {
      const currentItem = menuItems.find(item => item.id === itemId);
      if (!currentItem) return;

      // Find items in the same category and branch
      const sameItems = menuItems.filter(item => 
        item.category === currentItem.category && 
        item.branchId === currentItem.branchId
      ).sort((a, b) => (a.order || 0) - (b.order || 0));

      const currentIndex = sameItems.findIndex(item => item.id === itemId);
      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= sameItems.length) return;

      // Swap orders
      const itemToSwap = sameItems[newIndex];
      const currentOrder = currentItem.order || 0;
      const swapOrder = itemToSwap.order || 0;

      // Update in Firebase
      await updateDoc(doc(db, 'menuItems', itemId), { order: swapOrder });
      await updateDoc(doc(db, 'menuItems', itemToSwap.id), { order: currentOrder });

      // Refresh the list
      fetchMenuItems();
    } catch (error) {
      console.error('Error updating item order:', error);
      alert('Failed to update item order');
    }
  };

  const exportToExcel = async () => {
    try {
      // Fetch all menu items for export (not just current category)
      const menuRef = collection(db, 'menuItems');
      const querySnapshot = await getDocs(menuRef);
      const allItems = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const exportData = allItems.map(item => ({
        'Item Name (English)': item.name || '',
        'Item Name (Arabic)': item.title_ar || item.name || '',
        'Description (English)': item.description || '',
        'Description (Arabic)': item.description_ar || item.description || '',
        'Price (QAR)': item.price || '',
        'Category': item.category || '',
        'Branch': item.branchId || '',
        'Type': item.type || '',
        'Calories': item.nutritionalInfo?.calories || '',
        'Display Order': item.order || 0,
        'Popular': item.isPopular ? 'Yes' : 'No',
        'New': item.isNew ? 'Yes' : 'No',
        'Image URL': item.imageUrl || '',
        'Ingredients': Array.isArray(item.ingredients) ? item.ingredients.join(', ') : '',
        'Created': item.createdAt ? new Date(item.createdAt.toDate()).toLocaleDateString() : '',
        'Updated': item.updatedAt ? new Date(item.updatedAt.toDate()).toLocaleDateString() : ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Menu Items');

      const fileName = `menu_items_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      alert(`Excel file exported successfully: ${fileName}`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  return (
    <div className="admin-menu-redesign">
      {/* Header */}
      <div className="menu-header">
        <h1>Menu Management</h1>
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '2px solid #dee2e6',
          borderRadius: '8px',
          padding: '8px 16px',
          marginBottom: '10px',
          fontSize: '14px',
          fontWeight: '600',
          color: '#495057'
        }}>
          Currently Managing: <span style={{ color: '#ff6b35', fontWeight: '700' }}>
            {branches.find(b => (b.originalId || b.name) === selectedBranch)?.name || selectedBranch}
          </span>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => {
              console.log('Branch Management button clicked! Branches:', branches);
              setShowBranchManager(true);
            }}
            style={{
              backgroundColor: '#ff6b35',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              marginRight: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            🔧 Rename Branches
          </button>
          
          <button 
            className="btn-import"
            onClick={() => setShowImportModal(true)}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginRight: '12px'
            }}
          >
            <Upload size={16} />
            Import Excel
          </button>
          
          <button 
            className="btn-export"
            onClick={exportToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginRight: '12px'
            }}
          >
            <Download size={16} />
            Export to Excel
          </button>
          
          <button 
            onClick={() => setShowDatabaseCleaner(true)}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginRight: '12px'
            }}
          >
            <Trash2 size={16} />
            Clear Database
          </button>
          <button 
            onClick={() => setShowCategoryManager(true)}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginRight: '12px'
            }}
          >
            <Edit2 size={16} />
            Manage Categories
          </button>
          <button 
            className="btn-add-item"
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus size={20} />
            Add Menu Item
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filters-left">
          <div className="filter-group">
            <label>Branch</label>
            <select 
              value={selectedBranch} 
              onChange={(e) => setSelectedBranch(e.target.value)}
              disabled={branchesLoading}
            >
              {branchesLoading ? (
                <option value="">Loading branches...</option>
              ) : branches.length === 0 ? (
                <option value="">No branches found</option>
              ) : (
                branches.map(branch => (
                  <option key={branch.id} value={branch.originalId || branch.name}>
                    {branch.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {getAllCategories().map(cat => (
                <option key={cat.name} value={cat.name}>
                  {cat.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="search-box">
          <Search size={18} />
          <input 
            type="text"
            placeholder="Search menu items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat">
          <span className="stat-label">Total Items</span>
          <span className="stat-value">{filteredItems.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Popular</span>
          <span className="stat-value">
            {filteredItems.filter(item => item.isPopular).length}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">New</span>
          <span className="stat-value">
            {filteredItems.filter(item => item.isNew).length}
          </span>
        </div>
      </div>

      {/* Menu List */}
      <div className="menu-list-container">
        {loading ? (
          <div className="loading-state">Loading menu items...</div>
        ) : filteredItems.length === 0 ? (
          <div className="empty-state">
            <p>No menu items found</p>
            <button 
              className="btn-add-first"
              onClick={() => setIsAddModalOpen(true)}
            >
              Add First Item
            </button>
          </div>
        ) : (
          <div className="menu-list">
            {/* List Header */}
            <div className="list-header">
              <div className="col-order">#</div>
              <div className="col-image">Image</div>
              <div className="col-name">Name</div>
              <div className="col-description">Description</div>
              <div className="col-price">Price</div>
              <div className="col-status">Status</div>
              <div className="col-actions">Actions</div>
            </div>

            {/* List Items */}
            {filteredItems.map((item) => (
              <div key={item.id} className="list-item">
                <div className="col-order">{item.order || 0}</div>
                
                <div className="col-image">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} />
                  ) : (
                    <div className="no-image">No Image</div>
                  )}
                </div>
                
                <div className="col-name">
                  <span className="item-name">{item.name}</span>
                  {item.title_ar && (
                    <span className="item-name-ar">{item.title_ar}</span>
                  )}
                </div>
                
                <div className="col-description">
                  <p>{item.description || 'No description'}</p>
                </div>
                
                <div className="col-price">
                  QAR {item.price || '0.00'}
                </div>
                
                <div className="col-status">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      <input
                        type="checkbox"
                        checked={item.available !== false}
                        onChange={async (e) => {
                          const checked = e.target.checked;
                          // Optimistically update UI
                          setMenuItems(items => items.map(m => 
                            m.id === item.id ? { ...m, available: checked } : m
                          ));
                          try {
                            await updateDoc(doc(db, 'menuItems', item.id), { available: checked });
                          } catch (err) {
                            console.error('Error updating availability:', err);
                            // Revert if error
                            setMenuItems(items => items.map(m => 
                              m.id === item.id ? { ...m, available: !checked } : m
                            ));
                            alert('Failed to update availability');
                          }
                        }}
                        style={{ margin: '0' }}
                      />
                      {item.available !== false ? 'Visible' : 'Hidden'}
                    </label>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {item.isPopular && (
                        <span className="badge badge-popular">Popular</span>
                      )}
                      {item.isNew && (
                        <span className="badge badge-new">New</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="col-actions">
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {/* Order controls */}
                    <button
                      onClick={() => updateItemOrder(item.id, 'up')}
                      disabled={filteredItems.findIndex(i => i.id === item.id) === 0}
                      style={{
                        background: filteredItems.findIndex(i => i.id === item.id) === 0 ? '#e9ecef' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '4px 6px',
                        borderRadius: '4px',
                        cursor: filteredItems.findIndex(i => i.id === item.id) === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        minWidth: '24px'
                      }}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => updateItemOrder(item.id, 'down')}
                      disabled={filteredItems.findIndex(i => i.id === item.id) === filteredItems.length - 1}
                      style={{
                        background: filteredItems.findIndex(i => i.id === item.id) === filteredItems.length - 1 ? '#e9ecef' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '4px 6px',
                        borderRadius: '4px',
                        cursor: filteredItems.findIndex(i => i.id === item.id) === filteredItems.length - 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        minWidth: '24px'
                      }}
                      title="Move down"
                    >
                      ↓
                    </button>
                    
                    {/* Edit/Delete buttons */}
                    <button 
                      className="btn-action btn-edit"
                      onClick={() => handleEdit(item)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      className="btn-action btn-delete"
                      onClick={() => handleDelete(item.id)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}</h2>
              <button className="btn-close" onClick={resetForm}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="menu-form">
              <div className="form-grid">
                <div className="form-group">
                  <label className="required">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter menu item name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Arabic Name</label>
                  <input
                    type="text"
                    value={formData.title_ar}
                    onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                    placeholder="Enter Arabic name"
                  />
                </div>

                <div className="form-group">
                  <label className="required">Price (QAR)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: e.target.value})}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label className="required">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                  >
                    {getAllCategories().map(cat => (
                      <option key={cat.name} value={cat.name}>
                        {cat.displayName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows="3"
                    placeholder="Enter item description"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Arabic Description</label>
                  <textarea
                    value={formData.description_ar}
                    onChange={(e) => setFormData({...formData, description_ar: e.target.value})}
                    rows="3"
                    placeholder="Enter Arabic description"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Calories</label>
                  <input
                    type="text"
                    value={formData.calories}
                    onChange={(e) => setFormData({...formData, calories: e.target.value})}
                    placeholder="e.g., 250 cal"
                  />
                </div>

                <div className="form-group">
                  <label>Type</label>
                  <input
                    type="text"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    placeholder="e.g., Hot, Cold, Seasonal"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Item Status</label>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isPopular}
                        onChange={(e) => setFormData({...formData, isPopular: e.target.checked})}
                      />
                      Popular Item
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isNew}
                        onChange={(e) => setFormData({...formData, isNew: e.target.checked})}
                      />
                      New Item
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  <XCircle size={18} />
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  <Save size={18} />
                  {editingItem ? 'Update Item' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Branch Manager Modal */}
      {showBranchManager && (
        <div className="modal-overlay" onClick={() => setShowBranchManager(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Branches</h2>
              <button className="btn-close" onClick={() => setShowBranchManager(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="branch-manager">
              <p>Manage your branches:</p>
              
              {/* Add New Branch Section */}
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '2px dashed #dee2e6',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#28a745', fontWeight: '600' }}>
                  Add New Branch
                </h4>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
                  <input
                    type="text"
                    placeholder="Enter branch name (e.g., Downtown Location)"
                    style={{
                      flex: 1,
                      maxWidth: '300px',
                      padding: '12px',
                      border: '2px solid #e8e8e8',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        addNewBranch(e.target.value.trim());
                        e.target.value = '';
                      }
                    }}
                    id="newBranchInput"
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('newBranchInput');
                      if (input.value.trim()) {
                        addNewBranch(input.value.trim());
                        input.value = '';
                      }
                    }}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '12px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>
              </div>
              
              {branches.map((branch) => (
                <div key={branch.id} className="branch-item" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <label style={{ flex: 1, fontWeight: '600', color: '#333' }}>
                    Branch Name:
                  </label>
                  <input
                    type="text"
                    defaultValue={branch.name}
                    onBlur={(e) => {
                      if (e.target.value !== branch.name && e.target.value.trim()) {
                        updateBranchName(branch.id, e.target.value.trim());
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value !== branch.name && e.target.value.trim()) {
                        updateBranchName(branch.id, e.target.value.trim());
                        e.target.blur();
                      }
                    }}
                    style={{
                      flex: 2,
                      padding: '12px',
                      border: '2px solid #e8e8e8',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}
                  />
                  <button
                    onClick={() => openCategoryManager(branch.id)}
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginRight: '8px'
                    }}
                    title="Manage categories for this branch"
                  >
                    <Filter size={16} />
                    Categories
                  </button>
                  <button
                    onClick={() => deleteBranch(branch.id)}
                    disabled={branches.length <= 1}
                    style={{
                      backgroundColor: branches.length <= 1 ? '#ccc' : '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      cursor: branches.length <= 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    title={branches.length <= 1 ? 'Cannot delete the last branch' : 'Delete branch and all its menu items'}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              ))}
              
              <div style={{
                marginTop: '24px',
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#666'
              }}>
                <strong>Tips:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '16px' }}>
                  <li>Click "Add Branch" to create a new location</li>
                  <li>Press Enter or click outside the field to save name changes</li>
                  <li>Branch names will be updated across your entire menu system</li>
                  <li>Use meaningful names like "Downtown" or "Mall Location"</li>
                  <li>You need at least one branch - the last branch cannot be deleted</li>
                </ul>
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '24px' }}>
              <button 
                type="button" 
                className="btn-cancel" 
                onClick={() => setShowBranchManager(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Import Menu Items from Excel</h2>
              <button className="btn-close" onClick={() => setShowImportModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="import-content" style={{ padding: '24px' }}>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ marginBottom: '12px' }}>Expected Excel Format:</h3>
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  <p><strong>Required columns:</strong></p>
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li><strong>Item Name (English)</strong> or <strong>Name</strong></li>
                    <li><strong>Price (QAR)</strong> or <strong>Price</strong></li>
                    <li><strong>Category</strong> (COFFEE, MILKBASED, NON-COFFEE, etc.)</li>
                  </ul>
                  <p><strong>Optional columns:</strong></p>
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>Item Name (Arabic), Description (English), Description (Arabic)</li>
                    <li>Branch, Type, Display Order, Calories, Image URL, Ingredients</li>
                    <li>Popular (Yes/No), New (Yes/No)</li>
                  </ul>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Select Excel File:
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e8e8e8',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                />
              </div>

              {importPreview.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4>Preview (First 5 rows):</h4>
                  <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    border: '1px solid #e8e8e8',
                    borderRadius: '6px'
                  }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      fontSize: '12px'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          {Object.keys(importPreview[0] || {}).map(key => (
                            <th key={key} style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #e8e8e8',
                              textAlign: 'left',
                              fontWeight: '600'
                            }}>
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value, i) => (
                              <td key={i} style={{ 
                                padding: '8px', 
                                borderBottom: '1px solid #f0f0f0'
                              }}>
                                {String(value).substring(0, 50)}{String(value).length > 50 ? '...' : ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportPreview([]);
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginRight: '12px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={importFromExcel}
                  disabled={!importFile || importLoading}
                  style={{
                    backgroundColor: !importFile || importLoading ? '#ccc' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: !importFile || importLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {importLoading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Upload size={16} />
                      Import Data
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && managingBranchId && (
        <div className="modal-overlay" onClick={() => setShowCategoryManager(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Categories - {branches.find(b => b.id === managingBranchId)?.name}</h2>
              <button className="btn-close" onClick={() => setShowCategoryManager(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="category-manager" style={{ padding: '24px' }}>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Select which categories are available at this branch location:
              </p>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px',
                marginBottom: '24px'
              }}>
                {CATEGORIES.map((category) => {
                  const currentBranchCategories = branchCategories[managingBranchId] || CATEGORIES;
                  const isEnabled = currentBranchCategories.includes(category);
                  
                  return (
                    <label
                      key={category}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px',
                        border: `2px solid ${isEnabled ? '#28a745' : '#e9ecef'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: isEnabled ? '#f8fff9' : '#f8f9fa',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => {
                          const currentCategories = branchCategories[managingBranchId] || CATEGORIES;
                          let newCategories;
                          
                          if (e.target.checked) {
                            newCategories = [...currentCategories, category];
                          } else {
                            newCategories = currentCategories.filter(c => c !== category);
                          }
                          
                          updateBranchCategories(managingBranchId, newCategories);
                        }}
                        style={{
                          marginRight: '12px',
                          width: '18px',
                          height: '18px',
                          accentColor: '#28a745'
                        }}
                      />
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
                          {CATEGORY_DISPLAY_NAMES[category]}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>
                          {category}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div style={{
                padding: '16px',
                backgroundColor: '#e7f3ff',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#0c5aa6'
              }}>
                <strong>Note:</strong> Disabling a category will hide it from the customer menu, but existing menu items won't be deleted.
                You can always re-enable categories later.
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '24px' }}>
              <button 
                type="button" 
                onClick={() => setShowCategoryManager(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryManager && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              padding: '0 8px'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>Category Management</h2>
              <button
                onClick={() => {
                  setShowCategoryManager(false);
                  setEditingCategory(null);
                  setNewCategoryName('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Add New Category */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Add New Category</h3>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Category Name
                  </label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Enter category name"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && addCategory()}
                  />
                </div>
                <button
                  onClick={addCategory}
                  disabled={!newCategoryName.trim()}
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: newCategoryName.trim() ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                    opacity: newCategoryName.trim() ? 1 : 0.6
                  }}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Categories List */}
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>All Categories</h3>
              
              {/* All Categories */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '500', color: '#6c757d' }}>
                  All Categories
                </h4>
                
                {/* Default Categories - Now Editable */}
                {CATEGORIES.map((category, index) => (
                  <div
                    key={category}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: '#f8f9fa',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      {editingCategory === category ? (
                        <input
                          type="text"
                          defaultValue={CATEGORY_DISPLAY_NAMES[category] || category}
                          onBlur={(e) => {
                            if (e.target.value.trim() && e.target.value !== (CATEGORY_DISPLAY_NAMES[category] || category)) {
                              // Update display name in the constants (this would need backend support for persistence)
                              CATEGORY_DISPLAY_NAMES[category] = e.target.value.trim();
                            }
                            setEditingCategory(null);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                          }}
                          autoFocus
                          style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            border: '2px solid #007bff',
                            borderRadius: '4px',
                            padding: '4px 8px'
                          }}
                        />
                      ) : (
                        <span style={{ fontWeight: '600', fontSize: '16px' }}>
                          {CATEGORY_DISPLAY_NAMES[category] || category}
                        </span>
                      )}
                      <span style={{ 
                        marginLeft: '12px', 
                        fontSize: '12px', 
                        backgroundColor: '#6c757d',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px'
                      }}>
                        BUILT-IN
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* Edit button */}
                      <button
                        onClick={() => setEditingCategory(category)}
                        style={{
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Edit2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Custom Categories */}
                {customCategories.map((category, index) => (
                    <div
                      key={category.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        backgroundColor: 'white',
                        border: '2px solid #dee2e6',
                        borderRadius: '8px',
                        marginBottom: '8px'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        {editingCategory === category.id ? (
                          <input
                            type="text"
                            defaultValue={category.displayName}
                            onBlur={(e) => {
                              if (e.target.value.trim() && e.target.value !== category.displayName) {
                                updateCategory(category.id, {
                                  displayName: e.target.value.trim(),
                                  name: e.target.value.toUpperCase().replace(/\s+/g, '_')
                                });
                              }
                              setEditingCategory(null);
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            autoFocus
                            style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              border: '2px solid #007bff',
                              borderRadius: '4px',
                              padding: '4px 8px'
                            }}
                          />
                        ) : (
                          <span style={{ fontWeight: '600', fontSize: '16px' }}>
                            {category.displayName}
                          </span>
                        )}
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* Order controls */}
                        <button
                          onClick={() => moveCategoryOrder(category.id, 'up')}
                          disabled={index === 0}
                          style={{
                            background: index === 0 ? '#e9ecef' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveCategoryOrder(category.id, 'down')}
                          disabled={index === customCategories.length - 1}
                          style={{
                            background: index === customCategories.length - 1 ? '#e9ecef' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            cursor: index === customCategories.length - 1 ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ↓
                        </button>
                        
                        {/* Edit button */}
                        <button
                          onClick={() => setEditingCategory(category.id)}
                          style={{
                            background: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <Edit2 size={14} />
                        </button>
                        
                        {/* Delete button */}
                        <button
                          onClick={() => deleteCategory(category.id, category.displayName)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {customCategories.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#6c757d',
                  fontSize: '16px'
                }}>
                  No custom categories yet. Add one above to get started.
                </div>
              )}
            </div>

            {/* Close button */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '2px solid #dee2e6'
            }}>
              <button
                onClick={() => {
                  setShowCategoryManager(false);
                  setEditingCategory(null);
                  setNewCategoryName('');
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Database Cleaner Modal */}
      {showDatabaseCleaner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '2px solid #dee2e6'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#333',
                margin: 0
              }}>Database Cleaner</h2>
              <button
                onClick={() => setShowDatabaseCleaner(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>
            
            <DatabaseCleaner />
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #dee2e6'
            }}>
              <button
                onClick={() => setShowDatabaseCleaner(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMenuPageRedesign;